import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import "jspdf-autotable";

function GetBooking() {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [shops, setShops] = useState([]);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [showDetails, setShowDetails] = useState({});
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [searchVendorId, setSearchVendorId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedPincode, setSelectedPincode] = useState("");
  const [pincodes, setPincodes] = useState([]);
  const [page, setPage] = useState(1);
  const [freelancers, setFreelancers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ category: "" });
  const [isPincodeOpen, setIsPincodeOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [isVendorOpen, setIsVendorOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [searchPincode, setSearchPincode] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [searchVendor, setSearchVendor] = useState("");
  const [searchShop, setSearchShop] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const limit = 30;

  // Refs for handling click outside to close dropdowns
  const pincodeDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const vendorDropdownRef = useRef(null);
  const shopDropdownRef = useRef(null);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pincodeDropdownRef.current &&
        !pincodeDropdownRef.current.contains(event.target)
      ) {
        setIsPincodeOpen(false);
      }
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target)
      ) {
        setIsCityOpen(false);
      }
      if (
        vendorDropdownRef.current &&
        !vendorDropdownRef.current.contains(event.target)
      ) {
        setIsVendorOpen(false);
      }
      if (
        shopDropdownRef.current &&
        !shopDropdownRef.current.contains(event.target)
      ) {
        setIsShopOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("No authentication token found. Please login again.");
          return;
        }

        console.log("Starting data fetch...");

        const [bookingRes, shopRes, categoryRes] = await Promise.all([
          fetch("https://visitguru.in/api/v1/bookservice/userbookget", {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(
            "https://visitguru.in/api/v1/freelancer/getAllfreelancer",
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          ),
          fetch("https://visitguru.in/api/v1/category/getCategory", {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
        ]);

        console.log("API Response Status:", {
          booking: bookingRes.status,
          shop: shopRes.status,
          category: categoryRes.status,
        });

        if (!bookingRes.ok) {
          const errorText = await bookingRes.text();
          console.error("Booking API Error:", errorText);
          throw new Error(
            `Booking fetch failed: ${bookingRes.status} - ${errorText}`
          );
        }
        if (!shopRes.ok) {
          const errorText = await shopRes.text();
          console.error("Shop API Error:", errorText);
          throw new Error(
            `Shop fetch failed: ${shopRes.status} - ${errorText}`
          );
        }
        if (!categoryRes.ok) {
          const errorText = await categoryRes.text();
          console.error("Category API Error:", errorText);
          throw new Error(
            `Category fetch failed: ${categoryRes.status} - ${errorText}`
          );
        }

        const bookingData = await bookingRes.json();
        const shopData = await shopRes.json();
        const categoryData = await categoryRes.json();

        console.log("API Response Data:", {
          bookingData: bookingData,
          shopData: shopData,
          categoryData: categoryData,
        });

        if (bookingData.success && Array.isArray(bookingData.data)) {
          console.log(`Found ${bookingData.data.length} bookings`);
          const allBookings = bookingData.data.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setBookings(allBookings);
          setFilteredBookings(allBookings);

          const uniqueCitiesObj = {};
          allBookings.forEach((b) => {
            const city = b.user?.city?.trim();
            if (city) {
              const key = city.toLowerCase();
              if (!uniqueCitiesObj[key]) {
                uniqueCitiesObj[key] =
                  city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
              }
            }
          });
          setCities(Object.values(uniqueCitiesObj));

          const uniqueStatuses = [
            ...new Set(allBookings.map((b) => b.status).filter(Boolean)),
          ];
          const uniquePincodes = [
            ...new Set(
              allBookings.map((b) => b.user?.pincode).filter(Boolean)
            ),
          ];

          setStatusOptions(uniqueStatuses);
          setPincodes(uniquePincodes);
        } else {
          console.error("Booking data structure:", bookingData);
          toast.error(
            `No bookings data found. API returned: ${JSON.stringify(
              bookingData
            )}`
          );
        }

        if (shopData.success && Array.isArray(shopData.data)) {
          console.log(`Found ${shopData.data.length} shops`);
          const enrichedShops = shopData.data.map((shop) => ({
            ...shop,
            shopName: shop.shopName || `Shop_${shop._id}` || "N/A",
          }));
          setShops(enrichedShops);
          setFreelancers(enrichedShops);
        } else {
          console.error("Shop data structure:", shopData);
          toast.error(
            `No shop data found. API returned: ${JSON.stringify(shopData)}`
          );
        }

        if (categoryData.success && Array.isArray(categoryData.data)) {
          console.log(`Found ${categoryData.data.length} categories`);
          setCategories(categoryData.data || []);
        } else {
          console.error("Category data structure:", categoryData);
          toast.error(
            `No category data found. API returned: ${JSON.stringify(
              categoryData
            )}`
          );
        }

        await fetchServicesByCategory(token, filters.category);
      } catch (err) {
        console.error("Data fetch error:", err);
        toast.error("Failed to load data: " + err.message);
      }
    };

    const fetchServicesByCategory = async (token, category) => {
      try {
        const url = category
          ? `https://visitguru.in/api/v1/service/getService?category=${category}`
          : "https://visitguru.in/api/v1/service/getService";

        console.log("Fetching services from:", url);

        const serviceRes = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        console.log("Service API Response Status:", serviceRes.status);

        if (!serviceRes.ok) {
          const errorText = await serviceRes.text();
          console.error("Service API Error:", errorText);
          throw new Error(
            `Service fetch failed: ${serviceRes.status} - ${errorText}`
          );
        }

        const serviceData = await serviceRes.json();
        console.log("Service data received:", serviceData);

        if (serviceData.success && Array.isArray(serviceData.data)) {
          console.log(`Found ${serviceData.data.length} services`);
          setServices(serviceData.data || []);
        } else {
          console.error("Service data structure:", serviceData);
          toast.error(
            `Failed to load services. API returned: ${JSON.stringify(
              serviceData
            )}`
          );
        }
      } catch (err) {
        console.error("Error fetching services:", err);
        toast.error("Failed to load services: " + err.message);
      }
    };

    fetchData();
  }, [filters.category]);

  useEffect(() => {
    const fetchServicesByCategory = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("No authentication token found");
          return;
        }

        console.log("Fetching services for category:", filters.category);

        const res = await fetch(
          `https://visitguru.in/api/v1/service/getService?category=${filters.category}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Category services response status:", res.status);

        if (!res.ok) {
          const errorText = await res.text();
          console.error("Category services error:", errorText);
          throw new Error(`Failed to fetch services: ${res.status}`);
        }

        const serviceData = await res.json();
        console.log("Category services data:", serviceData);

        if (serviceData.success && Array.isArray(serviceData.data)) {
          setServices(serviceData.data || []);
        } else {
          console.error("Invalid service data structure:", serviceData);
          toast.error("Failed to load services for selected category");
          setServices([]);
        }
      } catch (err) {
        console.error("Error fetching services by category:", err);
        toast.error("Failed to load services: " + err.message);
        setServices([]);
      }
    };

    if (filters.category) {
      fetchServicesByCategory();
    } else {
      const fetchAllServices = async () => {
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            toast.error("No authentication token found");
            return;
          }

          console.log("Fetching all services");

          const res = await fetch(
            "https://visitguru.in/api/v1/service/getService",
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          console.log("All services response status:", res.status);

          if (!res.ok) {
            const errorText = await res.text();
            console.error("All services error:", errorText);
            throw new Error(`Failed to fetch services: ${res.status}`);
          }

          const serviceData = await res.json();
          console.log("All services data:", serviceData);

          if (serviceData.success && Array.isArray(serviceData.data)) {
            setServices(serviceData.data || []);
          } else {
            console.error("Invalid service data structure:", serviceData);
            toast.error("Failed to load all services");
            setServices([]);
          }
        } catch (err) {
          console.error("Error fetching all services:", err);
          toast.error("Failed to load services: " + err.message);
          setServices([]);
        }
      };
      fetchAllServices();
    }
  }, [filters.category]);

  const getTotalBookingPrice = (services) => {
    if (!Array.isArray(services)) return 0;
    return services.reduce((total, svc) => total + (svc.subtotal || svc.price * (svc.quantity || 1) || 0), 0);
  };

  useEffect(() => {
    let filtered = [...bookings];

    if (selectedCity) {
      filtered = filtered.filter(
        (b) => b.user?.city?.toLowerCase() === selectedCity.toLowerCase()
      );
    }

    if (selectedVendor) {
      filtered = filtered.filter((b) => {
        if (!b.shop) return false;
        return String(b.shop?._id || b.shop) === String(selectedVendor);
      });
    }

    if (searchVendorId) {
      filtered = filtered.filter((b) => {
        return String(b.staticVendorId) === String(searchVendorId);
      });
    }

    if (selectedStatus) {
      filtered = filtered.filter((b) => b.status === selectedStatus);
    }

    if (fromDate && toDate) {
      filtered = filtered.filter((b) => {
        const bookingDate = new Date(b.createdAt);
        const startDate = new Date(fromDate + "T00:00:00");
        const endDate = new Date(toDate + "T23:59:59");
        return bookingDate >= startDate && bookingDate <= endDate;
      });
    } else if (fromDate) {
      filtered = filtered.filter(
        (b) => new Date(b.createdAt) >= new Date(fromDate + "T00:00:00")
      );
    } else if (toDate) {
      filtered = filtered.filter(
        (b) => new Date(b.createdAt) <= new Date(toDate + "T23:59:59")
      );
    }

    if (selectedPincode) {
      filtered = filtered.filter((b) => b.user?.pincode === selectedPincode);
    }

    if (filters.category) {
      filtered = filtered.filter((b) => {
        if (!b.services) return false;
        return b.services.some(
          (s) => String(s?.categoryName) === String(filters.category)
        );
      });
    }

    if (selectedShop) {
      filtered = filtered.filter((b) => {
        if (!b.shopName) return false;
        return String(b.shopName) === String(selectedShop);
      });
    }

    setFilteredBookings(filtered);
    setPage(1);
  }, [
    selectedCity,
    selectedVendor,
    searchVendorId,
    selectedStatus,
    fromDate,
    toDate,
    selectedPincode,
    selectedShop,
    bookings,
    filters.category,
    freelancers,
  ]);

  const getUserName = (user) => {
    if (!user) return "N/A";
    const firstName = user.firstName || "";
    const lastName = user.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user.fullName || user.name || "N/A";
  };

  const getServiceNames = (services) => {
    if (!services || !Array.isArray(services)) return "N/A";
    const names = services
      .map((item) => item.serviceName || "Unknown Service")
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : "N/A";
  };

  const getVendorId = (booking) => {
    return booking.staticVendorId || "N/A";
  };

  const getShopId = (shop) => {
    if (!shop) return "N/A";
    if (typeof shop === "string") return shop;
    return shop._id || "N/A";
  };

  const getShopName = (shop, shopName) => {
    if (!shop && !shopName) return "N/A";
    if (shopName) return shopName;
    if (typeof shop === "object" && shop?.shopName) {
      return shop.shopName;
    }
    if (typeof shop === "string") {
      const found = shops.find((s) => s._id === shop);
      return found?.shopName || `Shop_${shop}` || "N/A";
    }
    return "N/A";
  };

  const toggleDetails = (id) =>
    setShowDetails((prev) => ({ ...prev, [id]: !prev[id] }));

  const currentBookings = filteredBookings.slice(
    (page - 1) * limit,
    page * limit
  );
  const totalPages = Math.ceil(filteredBookings.length / limit);

  const vendorOptions = shops.map((shop) => ({
    value: shop._id,
    label:
      shop.shopName ||
      `${shop.firstName || ""} ${shop.lastName || ""}`.trim() ||
      "N/A",
  }));

  const pincodeOptions = pincodes.map((pincode) => ({
    value: pincode,
    label: pincode,
  }));

  const cityOptions = cities.map((city) => ({
    value: city,
    label: city,
  }));

  const filteredShops = [
    ...new Set(bookings.map((b) => b.shopName).filter(Boolean)),
  ]
    .map((shopName) => ({
      value: shopName,
      label: shopName,
    }))
    .filter((shop) =>
      shop.label.toLowerCase().includes(searchShop.toLowerCase())
    );

  const filteredPincodes = pincodeOptions.filter((pincode) =>
    pincode.label.toLowerCase().includes(searchPincode.toLowerCase())
  );

  const filteredCities = cityOptions.filter((city) =>
    city.label.toLowerCase().includes(searchCity.toLowerCase())
  );

  const filteredVendors = vendorOptions.filter((vendor) =>
    vendor.label.toLowerCase().includes(searchVendor.toLowerCase())
  );

  const downloadPDF = () => {
    // Universal legal size — you can change it dynamically if needed
    const doc = new jsPDF({
      orientation: "landscape", // better for many columns
      unit: "mm",
      format: [215.9, 355.6], // width × height in mm (Legal)
    });

    doc.setFontSize(16);
    doc.text("Bookings Report", 14, 15);

    const tableColumn = [
      "No",
      "User Name",
      "Vendor ID",
      "Shop Name",
      "Mobile",
      "City",
      "Services",
      "Price",
      "Status",
    ];

    const tableRows = currentBookings.map((booking, index) => {
      return [
        (page - 1) * limit + index + 1, // No
        getUserName(booking.user), // User Name
        getVendorId(booking), // Vendor ID
        getShopName(booking.shop, booking.shopName), // Shop Name
        booking.user?.mobileNumber || "N/A", // Mobile (updated to mobileNumber)
        booking.user?.city || "N/A", // City
        getServiceNames(booking.services), // Services
        `${getTotalBookingPrice(booking.services).toFixed(2)}₹`, // Price
        booking.status || "N/A", // Status
      ];
    });

    if (tableRows.length > 0) {
      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 25,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [8, 132, 196], textColor: [255, 255, 255] },
        tableWidth: "auto",
      });

      doc.save("bookings_report.pdf");
    } else {
      toast.error("No data available to generate PDF.");
    }
  };

  return (
    <div className="space-y-12">
      <div className="relative flex flex-col bg-white w-full shadow-lg rounded">
        <div className="rounded-t px-4 py-3 border-0 bg-white">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-base text-[#0884c4]">
              Bookings ({filteredBookings.length})
            </h3>
            <button
              style={{
                backgroundColor: "#0884c4",
                width: "100px",
                height: "40px",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                outline: "none",
              }}
              onClick={downloadPDF}
              className="bg-[#0884c4] text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Download PDF
            </button>
          </div>

          {/* Filters */}
          <div className="mt-4 space-y-4">
            <div
              className="flex flex-wrap gap-2 items-center"
              style={{ marginBottom: "15px" }}
            >
              <div
                className="flex flex-col w-1/5"
                style={{ marginRight: "5px" }}
              >
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={filters.category}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-1 h-10 border border-blueGray-200 rounded focus:outline-none focus:ring text-sm"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category.categoryName}>
                      {category.categoryName || category.name || "N/A"}
                    </option>
                  ))}
                </select>
              </div>

              {/* Shop Dropdown */}
              <div
                className="relative w-1/5"
                ref={shopDropdownRef}
                style={{ marginRight: "5px" }}
              >
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shop Name
                </label>
                <div
                  className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer focus:outline-none focus:ring-blueGray"
                  style={{ height: "38px" }}
                  onClick={() => {
                    setIsShopOpen((prev) => !prev);
                    setIsCityOpen(false);
                    setIsPincodeOpen(false);
                  }}
                >
                  {selectedShop || "Select Shop"}
                </div>
                {isShopOpen && (
                  <div
                    className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg"
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      scrollBehavior: "smooth",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search shop..."
                      value={searchShop}
                      onChange={(e) => setSearchShop(e.target.value)}
                      className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none"
                    />
                    <ul style={{ scrollBehavior: "smooth" }}>
                      <li
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold"
                        onClick={() => {
                          setSelectedShop("");
                          setIsShopOpen(false);
                          setPage(1);
                        }}
                      >
                        All Shops
                      </li>
                      {filteredShops.length > 0 ? (
                        filteredShops.map((shop) => (
                          <li
                            key={shop.value}
                            className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                            onClick={() => {
                              setSelectedShop(shop.value);
                              setIsShopOpen(false);
                              setPage(1);
                            }}
                          >
                            {shop.label}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-gray-500">
                          No shops found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* City Dropdown */}
              <div
                className="relative w-1/5"
                ref={cityDropdownRef}
                style={{ marginRight: "5px" }}
              >
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City/Village
                </label>
                <div
                  className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer focus:outline-none focus:ring-blueGray"
                  style={{ height: "38px" }}
                  onClick={() => {
                    setIsCityOpen((prev) => !prev);
                    setIsPincodeOpen(false);
                    setIsVendorOpen(false);
                    setIsShopOpen(false);
                  }}
                >
                  {selectedCity || "Select City"}
                </div>
                {isCityOpen && (
                  <div
                    className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg"
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      scrollBehavior: "smooth",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search city..."
                      value={searchCity}
                      onChange={(e) => setSearchCity(e.target.value)}
                      className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none"
                    />
                    <ul style={{ scrollBehavior: "smooth" }}>
                      <li
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold"
                        onClick={() => {
                          setSelectedCity("");
                          setIsCityOpen(false);
                          setPage(1);
                        }}
                      >
                        All Cities
                      </li>
                      {filteredCities.length > 0 ? (
                        filteredCities.map((city) => (
                          <li
                            key={city.value}
                            className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                            onClick={() => {
                              setSelectedCity(city.value);
                              setIsCityOpen(false);
                              setPage(1);
                            }}
                          >
                            {city.label}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-gray-500">
                          No cities found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Vendor Dropdown */}
              <div
                className="relative w-1/5"
                ref={vendorDropdownRef}
                style={{ marginRight: "5px" }}
              >
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor
                </label>
                <div
                  className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer focus:outline-none focus:ring-blueGray"
                  style={{ height: "38px" }}
                  onClick={() => {
                    setIsVendorOpen((prev) => !prev);
                    setIsPincodeOpen(false);
                    setIsCityOpen(false);
                    setIsShopOpen(false);
                  }}
                >
                  {vendorOptions.find((v) => v.value === selectedVendor)
                    ?.label || "Select Vendor"}
                </div>
                {isVendorOpen && (
                  <div
                    className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg"
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      scrollBehavior: "smooth",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search vendor..."
                      value={searchVendor}
                      onChange={(e) => setSearchVendor(e.target.value)}
                      className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none"
                    />
                    <ul style={{ scrollBehavior: "smooth" }}>
                      <li
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold"
                        onClick={() => {
                          setSelectedVendor("");
                          setIsVendorOpen(false);
                          setPage(1);
                        }}
                      >
                        All Vendors
                      </li>
                      {filteredVendors.length > 0 ? (
                        filteredVendors.map((vendor) => (
                          <li
                            key={vendor.value}
                            className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                            onClick={() => {
                              setSelectedVendor(vendor.value);
                              setIsVendorOpen(false);
                              setPage(1);
                            }}
                          >
                            {vendor.label}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-gray-500">
                          No vendors found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex flex-col" style={{ marginRight: "5px" }}>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Vendor ID
                </label>
                <input
                  type="text"
                  value={searchVendorId}
                  onChange={(e) => setSearchVendorId(e.target.value)}
                  placeholder="Search Vendor ID"
                  className="border rounded px-3 py-2"
                  style={{
                    width: "150px",
                    height: "38px",
                    fontSize: "0.875rem",
                  }}
                />
              </div>

              <div className="flex flex-col" style={{ marginRight: "5px" }}>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="border rounded px-3 py-2"
                  style={{
                    width: "150px",
                    height: "38px",
                    fontSize: "0.875rem",
                  }}
                >
                  <option value="">All Status</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col" style={{ marginRight: "5px" }}>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="border rounded px-3 py-2"
                  style={{
                    width: "150px",
                    height: "38px",
                    fontSize: "0.875rem",
                  }}
                />
              </div>

              <div className="flex flex-col" style={{ marginRight: "5px" }}>
                <label className="text-sm font-medium text-gray-700 mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="border rounded px-3 py-2"
                  style={{
                    width: "150px",
                    height: "38px",
                    fontSize: "0.875rem",
                  }}
                />
              </div>

              {/* Pincode Dropdown */}
              <div className="relative w-1/5" ref={pincodeDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pincode
                </label>
                <div
                  className="w-full px-3 py-1 border border-blueGray-200 rounded cursor-pointer focus:outline-none focus:ring-blueGray"
                  style={{ height: "38px" }}
                  onClick={() => {
                    setIsPincodeOpen((prev) => !prev);
                    setIsCityOpen(false);
                    setIsVendorOpen(false);
                    setIsShopOpen(false);
                  }}
                >
                  {selectedPincode || "Select Pincode"}
                </div>
                {isPincodeOpen && (
                  <div
                    className="absolute z-10 bg-white border border-blueGray-200 mt-1 w-full rounded shadow-lg"
                    style={{
                      maxHeight: "300px",
                      overflowY: "auto",
                      scrollBehavior: "smooth",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search pincode..."
                      value={searchPincode}
                      onChange={(e) => setSearchPincode(e.target.value)}
                      className="w-full px-3 py-2 border-b border-blueGray-200 focus:outline-none"
                    />
                    <ul style={{ scrollBehavior: "smooth" }}>
                      <li
                        className="px-3 py-2 hover:bg-blue-100 cursor-pointer font-semibold"
                        onClick={() => {
                          setSelectedPincode("");
                          setIsPincodeOpen(false);
                          setPage(1);
                        }}
                      >
                        All Pincodes
                      </li>
                      {filteredPincodes.length > 0 ? (
                        filteredPincodes.map((pincode) => (
                          <li
                            key={pincode.value}
                            className="px-3 py-2 hover:bg-blue-100 cursor-pointer"
                            onClick={() => {
                              setSelectedPincode(pincode.value);
                              setIsPincodeOpen(false);
                              setPage(1);
                            }}
                          >
                            {pincode.label}
                          </li>
                        ))
                      ) : (
                        <li className="px-3 py-2 text-gray-500">
                          No pincodes found
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  #
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  User Name
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  Vendor ID
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  Shop Name
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  Mobile
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  City/Village
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  Services
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  Price
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  Status
                </th>
                <th className="px-6 py-3 bg-blueGray-50 text-blueGray-500 border border-solid border-blueGray-100 text-xs uppercase font-semibold text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentBookings.map((booking, index) => {
                const shopId = getShopId(booking.shop);
                return (
                  <React.Fragment key={booking._id}>
                    <tr>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {(page - 1) * limit + index + 1}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {getUserName(booking.user)}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {getVendorId(booking)}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {getShopName(booking.shop, booking.shopName)}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {booking.user?.mobileNumber || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {booking.user?.city || "N/A"}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        {getServiceNames(booking.services)}
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        ₹{getTotalBookingPrice(booking.services).toFixed(2)}
                      </td>
                      <td className="px-4 py-4 text-xs border-t border-blueGray-100">
                        <span
                          className={`px-2 py-1 rounded text-xs ${booking.status === "Completed"
                            ? "bg-green-100 text-green-800"
                            : booking.status === "Pending"
                              ? "bg-yellow-100 text-yellow-800"
                              : booking.status === "Rejected"
                                ? "bg-red-100 text-red-800"
                                : booking.status === "Approved"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                        >
                          {booking.status || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs border-t border-blueGray-100">
                        <button
                          onClick={() => toggleDetails(booking._id)}
                          className="bg-lightBlue-500 text-white font-bold uppercase text-xs px-4 py-2 rounded shadow"
                        >
                          {showDetails[booking._id] ? "Hide" : "View"}
                        </button>
                      </td>
                    </tr>
                    {showDetails[booking._id] && (
                      <tr>
                        <td colSpan="8" className="px-6 pb-4">
                          <div className="bg-gray-50 p-4 rounded">
                            <h4 className="font-semibold mb-2">
                              Booking Details
                            </h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <p>
                                <strong>User:</strong>{" "}
                                {getUserName(booking.user)}
                              </p>
                              <p>
                                <strong>Mobile:</strong>{" "}
                                {booking.user?.mobileNumber || "N/A"}
                              </p>
                              <p>
                                <strong>City:</strong>{" "}
                                {booking.user?.city || "N/A"}
                              </p>
                              <p>
                                <strong>Address:</strong>{" "}
                                {booking.user?.address || "N/A"}
                              </p>
                              <p>
                                <strong>Pincode:</strong>{" "}
                                {booking.user?.pincode || "N/A"}
                              </p>
                              {/* <p>
                                <strong>Shop ID:</strong> {shopId}
                              </p> */}
                              <p>
                                <strong>Shop Name:</strong>{" "}
                                {getShopName(booking.shop, booking.shopName)}
                              </p>
                              <p>
                                <strong>Vendor ID:</strong>{" "}
                                {getVendorId(booking)}
                              </p>
                              <p>
                                <strong>Services:</strong>{" "}
                                {getServiceNames(booking.services)}
                              </p>

                              <p>
                                <strong>Created:</strong>{" "}
                                {new Date(booking.createdAt).toLocaleString()}
                              </p>
                              <p>
                                <strong>Updated:</strong>{" "}
                                {new Date(booking.updatedAt).toLocaleString()}
                              </p>
                              <p>
                                <strong>Status:</strong>{" "}
                                {booking.status || "N/A"}
                              </p>
                              <p>
                                <strong>Categories:</strong>{" "}
                                {Array.isArray(booking.services) &&
                                  booking.services.length > 0 ? (
                                  <span>
                                    {[
                                      ...new Set(
                                        booking.services
                                          .map(
                                            (svc) =>
                                              svc.categoryName
                                          )
                                          .filter(Boolean)
                                      ),
                                    ].join(", ")}
                                  </span>
                                ) : (
                                  "No Categories"
                                )}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center p-4 border-t">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="px-4 py-2 bg-gray-300 text-sm rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm">
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            className="px-4 py-2 bg-gray-300 text-sm rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default GetBooking;